import { useStudioStateQuery } from '#~/statequery/hook.js';
import { StudioRTMPDestinationsSelector } from '#~/statequery/selectors/device-rtmp-destinations.js';
import type { Credentials as M_Credentials } from '@studio/models/auth';
import { useMemo } from 'react';
import type { Destination } from '../store/destinations.js';

/**
 * Hook to retrieve RTMP destinations from the global agent state.
 *
 * This hook queries all devices across all agents and extracts their RTMP destinations,
 * transforming them into the local Destination interface format.
 *
 * @returns Array of destinations found in the global state
 */
export const useGlobalStudioRTMPDestinations = (): Destination[] => {
  const studioDestinations = useStudioStateQuery(StudioRTMPDestinationsSelector);

  return useMemo(() => {
    if (!studioDestinations) {
      return [];
    }

    const allDestinations: Destination[] = [];
    // Iterate through all devices and their destinations
    Object.values(studioDestinations.destinations).forEach((dest) => {
      // Transform global state destination to local Destination interface
      allDestinations.push({
        id: dest.id,
        nickName: dest.nickName,
        url: dest.url,
        streamKey: dest.streamKey,
        // Note: Global state doesn't store credential information
        // Default to no auth required for destinations loaded from global state
        requireAuth: false,
        credentials: {} as M_Credentials
      });
    });

    return allDestinations;
  }, [studioDestinations]);
};
